/**
 * Share control for the product page.
 *
 * Two behaviours, picked from what the device actually offers rather than from
 * a user-agent guess: where navigator.share exists -- every current phone --
 * the OS share sheet opens, which is the thing people already know how to use
 * and the only route to WhatsApp, which is where this catalogue gets shared.
 * Everywhere else the link goes to the clipboard and the button confirms it.
 *
 * Delegated from the document rather than bound to the button. The product
 * page re-renders through the Section Rendering API on every variant change,
 * which replaces this button with a new node; anything bound directly would be
 * listening to an element no longer in the page. The same fault that made
 * item-variant-swap inert inside the recommendations section.
 *
 * Loaded as a module, so however many tags point at this URL it is fetched and
 * executed exactly once.
 */
(function () {
  'use strict';

  var RESET_AFTER = 2000;

  function confirm(button) {
    button.classList.add('pdp-share--done');
    window.setTimeout(function () {
      button.classList.remove('pdp-share--done');
    }, RESET_AFTER);
  }

  /* Clipboard API is https-only and can be refused outright by permissions
     policy, in which case it rejects rather than throwing. The textarea route
     is the fallback that still works in those cases. */
  function copy(text, button) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(
        function () {
          confirm(button);
        },
        function () {
          legacyCopy(text, button);
        }
      );
      return;
    }
    legacyCopy(text, button);
  }

  function legacyCopy(text, button) {
    var field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    // Off-screen rather than hidden: a display:none field cannot be selected.
    field.style.cssText = 'position:fixed;inset-block-start:-9999px;opacity:0;';
    document.body.appendChild(field);
    field.select();
    try {
      document.execCommand('copy');
      confirm(button);
    } catch (e) {
      /* Nothing useful left to try. The button simply does not confirm, which
         is better than claiming a copy that did not happen. */
    }
    document.body.removeChild(field);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-pdp-share]');
    if (!button) return;

    event.preventDefault();

    var url = button.getAttribute('data-share-url');
    if (!url) return;
    var title = button.getAttribute('data-share-title') || document.title;

    if (navigator.share) {
      navigator.share({ title: title, url: url }).catch(function () {
        /* Dismissing the share sheet rejects the promise. That is a person
           changing their mind, not a failure, so it is swallowed rather than
           falling through to a clipboard copy they did not ask for. */
      });
      return;
    }

    copy(url, button);
  });
})();
